import { Tab, Tabs, styled } from "@mui/material";
import {
  Accessibility,
  LocalHospital,
  NoteAlt,
} from "@mui/icons-material";
import { ReactElement } from "react";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";

/**
 * Etapas do atendimento.
 * Cada etapa vira uma aba (Tab) com o seu ícone correspondente,
 * substituindo o antigo Stepper por uma navegação em abas mais moderna.
 */
const steps: { label: string; icon: ReactElement }[] = [
  { label: "Atendimento", icon: <Accessibility /> },
  { label: "Intervenções", icon: <LocalHospital /> },
  { label: "Documentos e assinatura", icon: <NoteAlt /> },
];

/**
 * Container das abas com indicador de linha inferior.
 * A aba selecionada é destacada por uma linha verde (primary800)
 * abaixo do rótulo, sem borda envolvendo o grupo de abas.
 */
const StyledTabs = styled(Tabs)(({ theme }) => ({
  width: "100%",
  minHeight: 40,
  flexShrink: 0,
  border: "none",
  boxShadow: "none",
  backgroundColor: "transparent",
  "& .MuiTabs-scroller": {
    border: "none",
  },
  "& .MuiTabs-flexContainer": {
    border: "none",
  },
  "& .MuiTab-root": {
    border: "none",
    boxShadow: "none",
    borderRadius: 0,
  },
  "& .MuiTabs-indicator": {
    height: 3,
    bottom: 0,
    borderRadius: "3px 3px 0 0",
    // Mesma cor primária do botão "Iniciar Teleatendimento" (primary800).
    backgroundColor: theme.palette.primary800.main,
  },
}));

/**
 * Aba individual. Texto em cor secundária por padrão e primary800
 * quando selecionada, combinando com o indicador de linha inferior.
 */
const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: 40,
  paddingTop: theme.spacing(0.75),
  paddingBottom: theme.spacing(0.75),
  textTransform: "none",
  fontWeight: 600,
  color: theme.palette.text.secondary,
  transition: "color .2s ease",
  // Espaço confortável entre ícone e rótulo.
  "& .MuiTab-iconWrapper": {
    marginRight: theme.spacing(1),
  },
  "&:hover": {
    color: theme.palette.primary800.main,
  },
  "&.Mui-selected": {
    color: theme.palette.primary800.main,
  },
}));

/**
 * Navegação das telas do atendimento em formato de abas.
 * Continua controlada pelo mesmo estado global (activeStep / setActiveStep)
 * do MedicalCareContext, sem criar novo estado.
 */
export function StepArea() {
  const { activeStep, setActiveStep } = useMedicalCareContext();

  return (
    <StyledTabs
      value={activeStep}
      onChange={(_, value) => setActiveStep(value)}
      // fullWidth aproveita todo o espaço horizontal disponível,
      // evitando espaços em branco excessivos entre as abas.
      variant="fullWidth"
      aria-label="Navegação das etapas do atendimento"
    >
      {steps.map((step) => (
        <StyledTab
          key={step.label}
          icon={step.icon}
          iconPosition="start"
          label={step.label}
        />
      ))}
    </StyledTabs>
  );
}
